import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import SEO from './src/components/SEO';
import Navbar from './src/components/layout/Navbar';
import Footer from './src/components/layout/Footer';
import FloatingActions from './src/components/layout/FloatingActions';
import { seoConfig } from './src/data/seoData';
import { homeFAQs } from './src/data/faqsData';
import { elevareCourses, elevareFirstYearPrograms, juniorCurriculum } from './src/data/coursesData';
import ImageSlider from './src/components/ui/ImageSlider';
import useFavicon from './src/hooks/useFavicon';
import LazyImage from './src/components/ui/LazyImage';
import LazyVideo from './src/components/ui/LazyVideo';

// Bespoke Live Stat Counter Component using requestAnimationFrame and IntersectionObserver
function Counter({ target, duration = 1500 }) {
  const [count, setCount] = useState(0);
  const elementRef = useRef(null);

  useEffect(() => {
    let start = 0;
    const end = parseInt(target, 10);
    if (isNaN(end)) return;

    let observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        let startTime = null;
        const animate = (timestamp) => {
          if (!startTime) startTime = timestamp;
          const progress = Math.min((timestamp - startTime) / duration, 1);
          setCount(Math.floor(progress * (end - start) + start));
          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            setCount(end);
          }
        };
        requestAnimationFrame(animate);
        observer.disconnect();
      }
    }, { threshold: 0.1 });

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={elementRef}>{count}</span>;
}

// Bespoke premium rotating words animation for the Hero Heading
function RotatingHeading() {
  const words = ["Future Career", "Learning Journey", "Academic Growth", "Startup Vision"];
  const [wordIdx, setWordIdx] = useState(0);
  const [subText, setSubText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let timer;
    const currentWord = words[wordIdx];
    const typingSpeed = isDeleting ? 40 : 85;

    if (isDeleting) {
      timer = setTimeout(() => {
        setSubText(currentWord.substring(0, subText.length - 1));
      }, typingSpeed);
    } else {
      timer = setTimeout(() => {
        setSubText(currentWord.substring(0, subText.length + 1));
      }, typingSpeed);
    }

    if (!isDeleting && subText === currentWord) {
      timer = setTimeout(() => setIsDeleting(true), 2200); // Pause on complete word
    } else if (isDeleting && subText === "") {
      setIsDeleting(false);
      setWordIdx((prev) => (prev + 1) % words.length);
    }

    return () => clearTimeout(timer);
  }, [subText, isDeleting, wordIdx]);

  return (
    <span className="inline-block relative py-1 text-gradient-gold-green-blue font-extrabold tracking-tight min-w-[180px] min-[360px]:min-w-[220px] sm:min-w-[280px]">
      {subText}
      <span className="border-r-[3.5px] border-[#1F6FFF] ml-1.5 animate-pulse" style={{ verticalAlign: 'middle' }}></span>
    </span>
  );
}

// Reusable React hook for magnetic hover effect on buttons
function useMagnetic(multiplier = 0.25) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const { clientX, clientY } = e;
    const { left, top, width, height } = ref.current.getBoundingClientRect();
    const centerX = left + width / 2;
    const centerY = top + height / 2;
    const x = (clientX - centerX) * multiplier;
    const y = (clientY - centerY) * multiplier;
    setPos({ x, y });
  };

  const handleMouseLeave = () => {
    setPos({ x: 0, y: 0 });
  };

  return { ref, pos, handleMouseMove, handleMouseLeave };
}

// Main Landing Component
function CervelloEduverseLanding() {
  useFavicon('/logo-main.webp');
  const [activeFaq, setActiveFaq] = useState(null);
  const coursesContainerRef = useRef(null);
  const alumniContainerRef = useRef(null);

  const scrollCarousel = (direction) => {
    if (coursesContainerRef.current) {
      const scrollAmount = coursesContainerRef.current.clientWidth * 0.85;
      coursesContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollAlumniCarousel = (direction) => {
    if (alumniContainerRef.current) {
      const scrollAmount = alumniContainerRef.current.clientWidth * 0.85;
      alumniContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const getSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const homeCourses = [
    ...elevareCourses.slice(0, 2).map(c => ({
      ...c,
      category: "Elevare Academy",
      badgeColor: "bg-amber-500/10 text-amber-600 border border-amber-500/20"
    })),
    ...elevareFirstYearPrograms.slice(0, 2).map(c => ({
      ...c,
      category: "First-Year Program",
      badgeColor: "bg-[#0B1C2D]/5 text-[#0B1C2D] border border-[#0B1C2D]/10",
      available: true
    })),
    ...juniorCurriculum.slice(0, 2).map(c => ({
      ...c,
      category: "Junior Academy",
      badgeColor: "bg-[#00BFFF]/10 text-[#00BFFF] border border-[#00BFFF]/20",
      available: true,
      techs: c.features
    }))
  ];

  const homeAlumni = [
    {
      name: "Mohd Uzair",
      role: "EAPCET MPC Student",
      tag: "EAPCET Batch",
      avatar: "/avatar-normal-boy.webp",
      text: "The conceptual clarity I got here for mathematics and physics made a difference. The focus on solving previous years' questions helped me secure a 26,000 rank in EAPCET!",
      outcomeLabel: "EAPCET Stream & Rank",
      beforeAfter: "MPC Stream – EAPCET 2026: 26,000 Rank"
    },
    {
      name: "Simran Fatima",
      role: "EAPCET BiPC Student",
      tag: "EAPCET Batch",
      avatar: "/avatar-hijab-girl.webp",
      text: "Being part of the EAPCET batch was amazing. The focused study planner and interactive biology sessions helped me secure a 29,197 rank in EAPCET!",
      outcomeLabel: "EAPCET Stream & Rank",
      beforeAfter: "BiPC Stream – EAPCET 2026: 29,197 Rank"
    },
    {
      name: "Ayesha",
      role: "BiPC 1st Year Student",
      tag: "Board Batch",
      avatar: "/avatar-hijab-girl.webp",
      text: "The conceptual approach to Zoology and Botany is what helped me score a massive 95% in my 1st Year BiPC boards! Best intermediate coaching ever.",
      outcomeLabel: "Board Exam Score",
      beforeAfter: "BiPC Stream – 95% Board Score (2025-2026)"
    },
    {
      name: "Usama",
      role: "MPC 1st Year Student",
      tag: "Board Batch",
      avatar: "/avatar-normal-boy.webp",
      text: "The conceptual clarity in Mathematics and Physics made learning so interesting. Getting 80% in my MPC 1st Year boards is a great start, all thanks to Cervello Junior Academy!",
      outcomeLabel: "Board Exam Score",
      beforeAfter: "MPC Stream – 80% Board Score (2025-2026)"
    }
  ];

  useEffect(() => {
    let isHovered = false;
    const container = coursesContainerRef.current;

    const handleMouseEnter = () => { isHovered = true; };
    const handleMouseLeave = () => { isHovered = false; };

    if (container) {
      container.addEventListener("mouseenter", handleMouseEnter, { passive: true });
      container.addEventListener("mouseleave", handleMouseLeave, { passive: true });
    }

    const timer = setInterval(() => {
      if (coursesContainerRef.current && !isHovered) {
        const c = coursesContainerRef.current;
        const maxScroll = c.scrollWidth - c.clientWidth;
        if (c.scrollLeft >= maxScroll - 10) {
          c.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          c.scrollBy({ left: 380, behavior: 'smooth' });
        }
      }
    }, 5000);

    return () => {
      clearInterval(timer);
      if (container) {
        container.removeEventListener("mouseenter", handleMouseEnter);
        container.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);

  useEffect(() => {
    let isHovered = false;
    const container = alumniContainerRef.current;

    const handleMouseEnter = () => { isHovered = true; };
    const handleMouseLeave = () => { isHovered = false; };

    if (container) {
      container.addEventListener("mouseenter", handleMouseEnter, { passive: true });
      container.addEventListener("mouseleave", handleMouseLeave, { passive: true });
    }

    const timer = setInterval(() => {
      if (alumniContainerRef.current && !isHovered) {
        const c = alumniContainerRef.current;
        const maxScroll = c.scrollWidth - c.clientWidth;
        if (c.scrollLeft >= maxScroll - 10) {
          c.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          c.scrollBy({ left: 380, behavior: 'smooth' });
        }
      }
    }, 5500);

    return () => {
      clearInterval(timer);
      if (container) {
        container.removeEventListener("mouseenter", handleMouseEnter);
        container.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);

  const toggleFAQ = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const heroRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth > 1024);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  const primaryBtn = useMagnetic(0.28);
  const secondaryBtn = useMagnetic(0.32);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1024);
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize, { passive: true });

    const handleMouseMove = (e) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { left, top, width, height } = heroRef.current.getBoundingClientRect();
      const x = ((clientX - left) / width) * 2 - 1;
      const y = ((clientY - top) / height) * 2 - 1;
      setMousePos({ x, y });
      setCursorPos({
        x: clientX - left,
        y: clientY - top
      });
    };

    // Attach to window on desktop for smoother tracking outside borders
    const handleWindowMouseMove = (e) => {
      if (!isDesktop) return;
      handleMouseMove(e);
    };

    window.addEventListener("mousemove", handleWindowMouseMove, { passive: true });

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleWindowMouseMove);
    };
  }, [isDesktop]);

  return (
    <React.Fragment>
      <SEO {...seoConfig.home} faqData={homeFAQs} />

      <div className="font-inter bg-nm-light text-[#1E293B] min-h-screen relative" id="main-content">
        {/* Unified sticky nav header */}
        <Navbar />

        {/* Dynamic scroll progress tracking bar */}
        <div className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#C6A25E] via-amber-400 to-[#0B1C2D] z-[60] origin-left pointer-events-none"
          style={{
            transform: `scaleX(1)`,
            transformOrigin: '0%',
            animation: 'scrollProgress linear'
          }}
        />

        {/* Skip to main link for usability */}
        <a href="#hero" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:bg-white focus:text-[#0B1C2D] focus:p-4 focus:rounded-lg focus:shadow-lg focus:z-[100]">
          Skip to main content
        </a>

        {/* HOMEPAGE SLIDER */}
        <HomepageSlider />

        {/* ================= HERO ================= */}
        <section
          ref={heroRef}
          id="hero"
          className="relative overflow-hidden w-full min-h-[90vh] flex items-center py-20 lg:py-0 bg-nm-light"
          aria-label="Hero Section"
        >
          {/* SEAMLESS ASSETS BLENDING CONTAINER */}
          <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden select-none">
            {/* Transparent Premium Soft Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#FFFFFF]/60 via-[#FAF9F6]/40 to-[#EAE7DF]/20 backdrop-blur-[1px]" />

            {/* Highly Visible Background Video */}
            <LazyVideo
              src={isMobile ? "/videos/home-mobile.mp4" : "/videos/home-desktop.mp4"}
              poster="/logo-main.webp"
              className="absolute inset-0 w-full h-full object-cover z-0 pointer-events-none select-none opacity-[0.45]"
              placeholderBg="bg-transparent"
            />

            {/* Soft Sunrise Golden Glow */}
            <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#D4A017]/10 rounded-full blur-[130px] animate-sunrise-shift" />
            {/* Royal Blue Bottom Glow */}
            <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#1F6FFF]/5 rounded-full blur-[120px] animate-pulse-slow" style={{ animationDelay: '4s' }} />
          </div>

          {/* CURSOR SPOTLIGHT FOLLOW EFFECT (Desktop only) */}
          {isDesktop && (
            <div
              className="absolute rounded-full pointer-events-none blur-[140px] opacity-40 z-10"
              style={{
                left: `${cursorPos.x - 120}px`,
                top: `${cursorPos.y - 120}px`,
                width: '240px',
                height: '240px',
                background: 'radial-gradient(circle, rgba(31,111,255,0.22) 0%, rgba(212,160,23,0.15) 50%, transparent 100%)',
                transition: 'left 0.12s ease-out, top 0.12s ease-out'
              }}
            />
          )}

          <div className="relative max-w-[1500px] mx-auto px-6 w-full flex flex-col items-center justify-center min-h-[80vh] relative z-20 py-20">
            {/* Floating glass highlights removed */}

            {/* Centered Content Column */}
            <div
              className="relative z-20 text-center max-w-4xl mx-auto flex flex-col items-center"
              style={isDesktop ? {
                transform: `translate(${mousePos.x * -4}px, ${mousePos.y * -4}px)`,
                transition: 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
              } : {}}
            >
              {/* Header Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/85 border border-white/90 text-[#0F1B3D] text-xs font-semibold mb-8 shadow-sm">
                <span className="w-2 h-2 rounded-full bg-[#C6A25E] animate-pulse" />
                <span>Eduverse Learning Ecosystem</span>
              </div>

              {/* Main Centered Heading */}
              <h1 className="font-playfair font-bold text-[#0F1B3D] leading-[1.08] mb-8 text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl tracking-tight">
                <span className="block">Transform Your</span>
                <span className="block mt-2"><RotatingHeading /></span>
              </h1>

              {/* Centered Description */}
              <p className="text-[#0F1B3D]/80 text-base sm:text-lg md:text-xl leading-relaxed max-w-3xl mx-auto mb-10 font-light">
                One unified platform nurturing school students, future professionals, and business innovators — with conceptual clarity, verified mentorship, and scalable capability.
              </p>

              {/* Centered CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16 w-full max-w-md">
                <button
                  ref={primaryBtn.ref}
                  onMouseMove={primaryBtn.handleMouseMove}
                  onMouseLeave={primaryBtn.handleMouseLeave}
                  onClick={() => scrollToSection("programs")}
                  style={isDesktop ? {
                    transform: `translate(${primaryBtn.pos.x}px, ${primaryBtn.pos.y}px)`,
                    transition: 'transform 0.15s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                  } : {}}
                  className="group relative px-8 py-4.5 rounded-2xl glass-button-light font-bold w-full sm:w-auto text-center flex items-center justify-center gap-2 hover:scale-[1.03] cursor-pointer"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    Explore Programs
                    <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </span>
                </button>
                <a
                  ref={secondaryBtn.ref}
                  onMouseMove={secondaryBtn.handleMouseMove}
                  onMouseLeave={secondaryBtn.handleMouseLeave}
                  href="/book-demo"
                  style={isDesktop ? {
                    transform: `translate(${secondaryBtn.pos.x}px, ${secondaryBtn.pos.y}px)`,
                    transition: 'transform 0.15s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                  } : {}}
                  className="group relative px-8 py-4.5 rounded-2xl glass-button-light font-bold w-full sm:w-auto text-center flex items-center justify-center gap-2 hover:scale-[1.03]"
                >
                  <span className="relative z-10 flex items-center gap-1.5 text-[#D4A017]">
                    Book 7-Day Demo (₹99)
                    <span className="text-xs px-2 py-0.5 rounded bg-[#D4A017]/10 text-[#D4A017] group-hover:bg-[#D4A017] group-hover:text-white transition-colors font-medium">LIVE</span>
                  </span>
                </a>
              </div>

              {/* Centered Counter Grid */}
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 w-full">
                {[
                  { target: "500", label: "Students Trained", icon: "🎓", color: "from-[#1F6FFF]/10 to-[#B388FF]/5", iconColor: "text-[#1F6FFF]" },
                  { target: "50", label: "Skills Courses", icon: "📚", color: "from-[#D4A017]/10 to-[#F8F6F1]/5", iconColor: "text-[#D4A017]" },
                  { target: "95", suffix: "%", label: "Outcome Rate", icon: "🚀", color: "from-[#B388FF]/10 to-[#1F6FFF]/5", iconColor: "text-[#B388FF]" }
                ].map((stat, idx) => (
                  <div key={idx} className="flex items-center gap-3.5 glass-card-light px-5 py-3.5 rounded-2xl hover:scale-105 hover:-translate-y-1 transition-spring cursor-default">
                    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center text-lg shadow-sm flex-shrink-0`}>
                      <span className={stat.iconColor}>{stat.icon}</span>
                    </div>
                    <div className="text-left">
                      <p className="text-2xl font-bold text-[#0F1B3D] tracking-tight leading-none">
                        <Counter target={stat.target} />{stat.suffix || "+"}
                      </p>
                      <p className="text-[10px] font-bold text-[#0F1B3D]/50 mt-1 uppercase tracking-wider">{stat.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </section>

        {/* ================= TRUST BAR looping logo marquee ================= */}
        <section className="bg-white border-y border-gray-100 py-8 relative overflow-hidden" aria-label="Our Trusted Ecosystem Areas">
          <div className="max-w-7xl mx-auto px-6 mb-4 text-center">
            <span className="text-xs font-bold text-[#C6A25E] uppercase tracking-widest">Our Ecosystem Focus</span>
          </div>

          <div className="relative flex overflow-hidden w-full select-none py-2">
            {/* First Track */}
            <div className="flex shrink-0 gap-16 justify-around min-w-full animate-marquee-loop">
              {techLogos.map((logo, idx) => (
                <div key={idx} className="flex items-center gap-3 text-slate-500 hover:text-[#0B1C2D] transition-colors duration-300">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    {logo.svg}
                  </div>
                  <span className="text-sm font-semibold uppercase tracking-wider">{logo.name}</span>
                </div>
              ))}
            </div>
            {/* Second Track */}
            <div className="flex shrink-0 gap-16 justify-around min-w-full animate-marquee-loop" aria-hidden="true">
              {techLogos.map((logo, idx) => (
                <div key={`dup-${idx}`} className="flex items-center gap-3 text-slate-500 hover:text-[#0B1C2D] transition-colors duration-300">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    {logo.svg}
                  </div>
                  <span className="text-sm font-semibold uppercase tracking-wider">{logo.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= SPECIAL DIFFERENCE SECTIONS ================= */}
        <section className="bg-nm-light py-20 border-t border-slate-200/40" aria-label="Key features">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-center mb-12 text-[#0B1C2D]">
              What Makes Us Different
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { title: "Structured Academics", desc: "Comprehensive curriculum designed for board & entrance excellence.", icon: "🎓", gradient: "from-[#C6A25E] to-[#B8860B]" },
                { title: "Skill‑First Learning", desc: "Focus on build-centric software projects and mathematical clarity.", icon: "🧠", gradient: "from-sky-400 to-sky-600" },
                { title: "Career & Business", desc: "Equipping graduates to write real code and launch actual ventures.", icon: "🚀", gradient: "from-slate-800 to-slate-950" },
                { title: "On-demand Mentorship", desc: "Direct, personal support channels instead of recorded lecture dumps.", icon: "👤", gradient: "from-amber-400 to-amber-600" }
              ].map((item, idx) => (
                <div key={idx} className="group relative flex flex-col items-center p-8 nm-flat-light rounded-3xl border-transparent hover:-translate-y-1.5 transition-all duration-500 overflow-hidden">
                  {/* Subtle background glow */}
                  <div className={`absolute -bottom-10 w-24 h-24 rounded-full bg-gradient-to-t ${item.gradient} opacity-0 group-hover:opacity-10 blur-xl transition-opacity duration-500`} />

                  <div className={`w-14 h-14 rounded-full bg-gradient-to-r ${item.gradient} flex items-center justify-center text-white text-2xl mb-5 shadow-md group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    {item.icon}
                  </div>
                  <h3 className="font-bold text-[#0B1C2D] mb-3 text-lg group-hover:text-[#C6A25E] transition-colors duration-300">{item.title}</h3>
                  <p className="text-sm text-slate-500 text-center leading-relaxed font-light">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= MANIFESTO SECTION ================= */}
        <ManifestoSection />

        {/* ================= ABOUT ================= */}
        <section id="about" className="bg-nm-light py-24 relative overflow-hidden border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-16 items-center">

              <div className="animate-slide-up">
                <div className="inline-block bg-[#C6A25E]/10 border border-[#C6A25E]/20 text-[#C6A25E] px-4 py-1.5 rounded-full text-xs font-semibold mb-6">
                  Cervello Philosophy
                </div>
                <h2 className="font-playfair text-4xl md:text-5xl font-bold mb-6 text-[#0B1C2D] leading-tight">
                  Education That <span className="bg-gradient-to-r from-[#C6A25E] to-sky-500 bg-clip-text text-transparent">Evolves With the World</span>
                </h2>
                <p className="text-slate-600 leading-relaxed mb-8 text-lg font-light">
                  Cervello Eduverse is a future‑ready education ecosystem bringing academics, mentorship, and real‑world skills together under one premium learning experience.
                </p>

                <div className="space-y-4">
                  {[
                    "Personalized learning paths for every student",
                    "Expert mentorship from working industry professionals",
                    "Cutting-edge curriculum aligned with modern industry benchmarks"
                  ].map((feat, idx) => (
                    <div key={idx} className="flex items-center">
                      <span className="w-6 h-6 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center justify-center mr-3 text-xs font-bold">✓</span>
                      <p className="text-slate-700 text-sm font-medium">{feat}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                {[
                  { title: 'Vision', content: 'Empower learners with clarity, confidence, and future‑ready skills.', icon: '🎯', color: 'from-[#C6A25E] to-[#B8860B]' },
                  { title: 'Mission', content: 'Create a comprehensive learning ecosystem that nurtures potential and unlocks opportunities.', icon: '🚀', color: 'from-sky-400 to-sky-600' },
                  { title: 'Values', content: 'Excellence, Innovation, Integrity, and Student-Centricity guide everything we do.', icon: '💎', color: 'from-[#0B1C2D] to-[#1E293B]' }
                ].map((item, index) => (
                  <div key={item.title} className="nm-flat-light p-6 rounded-2xl border-transparent hover:scale-[1.02] hover:shadow-[8px_8px_16px_#cbd5e1,-8px_-8px_16px_#ffffff] transition-all duration-300 animate-slide-up" style={{ animationDelay: `${index * 0.15}s` }}>
                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${item.color} flex items-center justify-center text-white text-xl flex-shrink-0 shadow-sm`}>
                        {item.icon}
                      </div>
                      <div>
                        <h4 className="font-bold mb-1 text-[#0B1C2D] text-lg">{item.title}</h4>
                        <p className="text-sm text-slate-500 leading-relaxed">{item.content}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </section>

        {/* ================= PROGRAMS ================= */}
        <section
          id="programs"
          className="relative overflow-hidden py-24 bg-nm-light border-t border-slate-200/40"
        >
          <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-sky-300/10 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#C6A25E]/10 rounded-full blur-[120px] translate-x-1/2 translate-y-1/2" />

          <div className="relative max-w-[1450px] mx-auto px-6">

            {/* Section Heading */}
            <div className="text-center mb-20 animate-slide-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur-md border border-white shadow-md mb-6">
                <span className="w-2 h-2 rounded-full bg-[#C6A25E] animate-pulse" />
                <span className="text-xs sm:text-sm font-medium text-slate-700">
                  Comprehensive Learning Ecosystem
                </span>
              </div>

              <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-[#0B1C2D] mb-6 leading-tight">
                Our{" "}
                <span className="bg-gradient-to-r from-[#C6A25E] to-sky-500 bg-clip-text text-transparent">
                  Learning Ecosystem
                </span>
              </h2>

              <p className="text-slate-500 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed font-light">
                Explore our distinct specialized divisions designed to unlock your core potential.
              </p>
            </div>

            {/* Program Cards Grid */}
            <div className="grid md:grid-cols-3 gap-8">

              {/* JUNIOR ACADEMY */}
              <ProgramCard
                title="Cervello Junior Academy"
                theme="junior"
                description="Academics meets mentorship. Customized foundation courses and board prep modules designed to secure top success for school students."
                features={["Class 10th Board preparation", "Intermediate BiPC & MPC coaching", "EAPCET / NEET foundation metrics"]}
                badgeText="8th Class to Intermediate"
                btnLink="/junior-academy"
                btnText="Explore Academy"
                logoSrc="/junior academy-favicon/apple-touch-icon.png"
              />

              {/* ELEVARE ACADEMY */}
              <ProgramCard
                title="Cervello Elevare"
                theme="elevare"
                description="Bespoke engineering bootcamps and analytics tracks focused on upskilling students and graduates for high-value technical placements."
                features={["Full-Stack Web Development", "Data Analytics & ML paths", "Portfolio & Placement support"]}
                badgeText="Graduates & Job Seekers"
                btnLink="/elevare"
                btnText="Explore Careers"
                logoSrc="/elevare academy-favicon/apple-touch-icon.png"
              />

              {/* BUSINESS LAB */}
              <ProgramCard
                title="Cervello Business Lab"
                theme="business"
                description="Incubate and accelerate your business model. Connect directly to growth mentors, legal advisory structures, and dynamic GTM plans."
                features={["Startup Incubation program", "Business planning & roadmaps", "Website & App build services"]}
                badgeText="Founders & Companies"
                btnLink="/business-lab"
                btnText="Explore Lab"
                logoSrc="/business-lab-favicon/apple-touch-icon.png"
              />

            </div>
          </div>
        </section>

        {/* ================= FEATURED COURSES SECTION ================= */}
        <section id="featured-courses" className="py-24 bg-nm-light border-y border-slate-200/40 relative overflow-hidden">
          {/* Background glows */}
          <div className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-[#00BFFF]/5 rounded-full blur-[100px] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#C6A25E]/5 rounded-full blur-[80px] pointer-events-none" />

          <div className="max-w-[1450px] mx-auto px-6">
            {/* Header with Navigation Controls */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
              <div>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0B1C2D]/5 border border-[#0B1C2D]/10 text-[#0B1C2D] text-xs font-semibold uppercase tracking-wider mb-4">
                  <span className="w-2 h-2 rounded-full bg-[#C6A25E] animate-pulse" />
                  Our Learning Tracks
                </span>
                <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#0B1C2D]">
                  Featured <span className="bg-gradient-to-r from-[#0B1C2D] to-sky-500 bg-clip-text text-transparent">Courses</span>
                </h2>
              </div>

              <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
                {/* Explore button */}
                <Link
                  to="/courses"
                  className="px-6 py-3 rounded-xl nm-button-light font-semibold text-[#0B1C2D] text-sm transition-all duration-300 flex items-center gap-2 border-transparent"
                >
                  Explore More Courses
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>

                {/* Arrow buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => scrollCarousel('left')}
                    className="w-12 h-12 rounded-xl nm-button-light flex items-center justify-center border-transparent cursor-pointer"
                    aria-label="Scroll left"
                  >
                    ←
                  </button>
                  <button
                    onClick={() => scrollCarousel('right')}
                    className="w-12 h-12 rounded-xl nm-button-light flex items-center justify-center border-transparent cursor-pointer"
                    aria-label="Scroll right"
                  >
                    →
                  </button>
                </div>
              </div>
            </div>

            {/* Side scrolling Carousel container */}
            <div
              ref={coursesContainerRef}
              className="flex overflow-x-auto gap-8 pb-8 snap-x snap-mandatory scrollbar-hide scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {homeCourses.map((course, idx) => (
                <div
                  key={idx}
                  className="flex-shrink-0 w-[300px] sm:w-[350px] md:w-[380px] snap-start nm-flat-light rounded-3xl border-transparent hover:-translate-y-1.5 transition-all duration-500 overflow-hidden flex flex-col justify-between group"
                >
                  {/* Image slider block */}
                  <Link
                    to={`/course/${getSlug(course.title)}`}
                    className="relative aspect-video w-full overflow-hidden bg-slate-50 block"
                  >
                    <div className="w-full h-full group-hover:scale-105 transition-transform duration-700">
                      <ImageSlider
                        images={course.images}
                        alt={course.title}
                        className="w-full h-full"
                      />
                    </div>
                    <span className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-md shadow-sm text-white z-20 ${course.available
                      ? 'bg-emerald-500/95'
                      : 'bg-amber-500/95'
                      }`}>
                      {course.available ? "Available Now" : "Coming Soon"}
                    </span>
                  </Link>

                  {/* Body details */}
                  <div className="p-6 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase ${course.badgeColor}`}>
                          {course.category}
                        </span>
                        <span className="text-xl group-hover:scale-110 transition-transform duration-300 inline-block">{course.icon}</span>
                      </div>

                      <h3 className="text-xl font-bold text-[#0B1C2D] mb-2 leading-tight group-hover:text-[#C6A25E] transition-colors duration-300">
                        {course.title}
                      </h3>

                      <p className="text-slate-500 text-xs sm:text-sm font-light leading-relaxed mb-6 line-clamp-2">
                        {course.description}
                      </p>
                    </div>

                    <div>
                      {/* Tech items (modern inline badges) */}
                      <div className="flex flex-wrap gap-2 mb-6 min-h-[32px] items-center">
                        {course.techs && course.techs.slice(0, 3).map((tech, techIdx) => (
                          <span key={techIdx} className="px-2.5 py-1 nm-inset-light text-slate-600 text-xs font-semibold rounded-lg transition-all duration-300">
                            {tech}
                          </span>
                        ))}
                      </div>

                      {/* CTA button */}
                      {course.available ? (
                        <Link
                          to={`/course/${getSlug(course.title)}`}
                          className="w-full nm-button-light py-3 rounded-xl font-semibold border-transparent text-center block text-sm active:scale-98"
                        >
                          View Details
                        </Link>
                      ) : (
                        <button
                          disabled
                          className="w-full bg-slate-100 text-slate-400 py-3 rounded-xl font-semibold text-center block text-sm cursor-not-allowed"
                        >
                          Coming Soon
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Explore More Courses Navigation Card (Junior Academy style, no icons, arrow indicator) */}
              <div className="flex-shrink-0 w-[300px] sm:w-[350px] md:w-[380px] snap-start nm-flat-light p-8 rounded-3xl border-transparent hover:scale-[1.01] transition-all duration-300 flex flex-col justify-between min-h-[350px] group">
                <div>
                  <h3 className="text-xl font-bold mb-3 text-[#0B1C2D] group-hover:text-[#C6A25E] transition-colors duration-300">Explore More</h3>
                  <p className="text-slate-500 text-sm leading-relaxed font-light mb-6">
                    Browse our full catalog of school coaching programs, intermediate MPC/BiPC prep, and professional developer bootcamps.
                  </p>
                </div>
                <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Programs</span>
                  <Link
                    to="/courses"
                    className="w-11 h-11 rounded-full nm-button-light flex items-center justify-center text-[#C6A25E] hover:text-[#0B1C2D] text-lg font-bold active:scale-95 transition-all"
                    aria-label="View all courses"
                  >
                    →
                  </Link>
                </div>
              </div>
            </div>

            {/* Centered Explore More Courses Button under side scroll */}
            <div className="flex justify-center mt-12 animate-slide-up">
              <Link
                to="/courses"
                className="px-8 py-4.5 rounded-2xl nm-button-light text-[#0B1C2D] font-bold border-transparent hover:scale-[1.03] transition-all duration-300 text-center flex items-center justify-center gap-2"
              >
                Explore More Courses
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>
        </section>

        {/* ================= ALUMNI SUCCESS SECTION ================= */}
        <section id="alumni-success" className="py-24 bg-nm-light border-b border-slate-200/40 relative overflow-hidden">
          {/* Background glows */}
          <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-[#C6A25E]/5 rounded-full blur-[100px] pointer-events-none" />

          <div className="max-w-[1450px] mx-auto px-6">
            {/* Header with Navigation Controls */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
              <div>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C6A25E]/10 border border-[#C6A25E]/20 text-[#C6A25E] text-xs font-semibold uppercase tracking-wider mb-4">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Alumni Achievements
                </span>
                <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#0B1C2D]">
                  Our Successful <span className="bg-gradient-to-r from-[#C6A25E] to-amber-500 bg-clip-text text-transparent">Alumni</span>
                </h2>
              </div>

              <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
                {/* Explore button */}
                <Link
                  to="/testimonials"
                  className="px-6 py-3 rounded-xl nm-button-light font-semibold text-[#0B1C2D] text-sm transition-all duration-300 flex items-center gap-2 border-transparent"
                >
                  Explore Success Stories
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>

                {/* Arrow navigation buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => scrollAlumniCarousel('left')}
                    className="w-12 h-12 rounded-xl nm-button-light flex items-center justify-center border-transparent cursor-pointer"
                    aria-label="Scroll alumni left"
                  >
                    ←
                  </button>
                  <button
                    onClick={() => scrollAlumniCarousel('right')}
                    className="w-12 h-12 rounded-xl nm-button-light flex items-center justify-center border-transparent cursor-pointer"
                    aria-label="Scroll alumni right"
                  >
                    →
                  </button>
                </div>
              </div>
            </div>

            {/* Side scrolling Carousel container */}
            <div
              ref={alumniContainerRef}
              className="flex overflow-x-auto gap-8 pb-8 snap-x snap-mandatory scrollbar-hide scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {homeAlumni.map((alumni, idx) => (
                <div
                  key={idx}
                  className="flex-shrink-0 w-[300px] sm:w-[350px] md:w-[380px] snap-start nm-flat-light rounded-3xl p-8 border-transparent hover:-translate-y-1.5 transition-all duration-500 flex flex-col justify-between"
                >
                  <div>
                    {/* Header */}
                    <div className="flex justify-between items-start mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl overflow-hidden bg-[#C6A25E]/10 flex items-center justify-center text-2xl flex-shrink-0">
                          <LazyImage src={alumni.avatar} alt={alumni.name} className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <h4 className="font-bold text-[#0B1C2D]">{alumni.name}</h4>
                          <p className="text-xs text-slate-400 font-medium">{alumni.role}</p>
                        </div>
                      </div>
                      <span className="text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-100">
                        {alumni.tag}
                      </span>
                    </div>

                    {/* Ratings */}
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className="text-[#C6A25E] text-base">★</span>
                      ))}
                    </div>

                    {/* Quote */}
                    <p className="text-slate-600 text-sm leading-relaxed font-light mb-6 italic line-clamp-3">
                      "{alumni.text}"
                    </p>
                  </div>

                  {/* Outcome display */}
                  <div className="border-t border-slate-100 pt-5 mt-4">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">{alumni.outcomeLabel}</span>
                    <div className="font-semibold text-sm text-[#0B1C2D] mt-1.5 flex items-center gap-1.5">
                      <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                      {alumni.beforeAfter}
                    </div>
                  </div>
                </div>
              ))}

              {/* Explore Testimonials Navigation Card (Junior Academy style, no icons, arrow indicator) */}
              <div className="flex-shrink-0 w-[300px] sm:w-[350px] md:w-[380px] snap-start nm-flat-light p-8 rounded-3xl border-transparent hover:scale-[1.01] transition-all duration-300 flex flex-col justify-between min-h-[350px] group">
                <div>
                  <h3 className="text-xl font-bold mb-3 text-[#0B1C2D] group-hover:text-[#C6A25E] transition-colors duration-300">Success Stories</h3>
                  <p className="text-slate-500 text-sm leading-relaxed font-light mb-6">
                    Read deep-dive case studies and reviews from students, parents, developers, and founders who completed our concept-first mentorship blocks.
                  </p>
                </div>
                <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Testimonials</span>
                  <Link
                    to="/testimonials"
                    className="w-11 h-11 rounded-full nm-button-light flex items-center justify-center text-[#87CEEB] hover:text-[#0B1C2D] text-lg font-bold active:scale-95 transition-all"
                    aria-label="View all testimonials"
                  >
                    →
                  </Link>
                </div>
              </div>
            </div>

            {/* Centered Explore More Alumni Button under side scroll */}
            <div className="flex justify-center mt-12 animate-slide-up">
              <Link
                to="/testimonials"
                className="px-8 py-4.5 rounded-2xl nm-button-light text-[#0B1C2D] font-bold border-transparent hover:scale-[1.03] transition-all duration-300 text-center flex items-center justify-center gap-2"
              >
                Explore All Success Stories
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>
        </section>

        {/* ================= FOUNDER'S STORY SECTION ================= */}
        <FoundersStorySection />

        {/* ================= WHY WE'RE NEW SECTION ================= */}
        <WhyWereNewSection />

        {/* ================= FAQ BLOCK ================= */}
        <section className="bg-nm-light py-24 border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-playfair text-4xl md:text-5xl font-bold mb-4 text-[#0B1C2D] tracking-tight">
                General <span className="bg-gradient-to-r from-[#C6A25E] to-sky-500 bg-clip-text text-transparent">FAQ</span>
              </h2>
              <p className="text-lg text-slate-500 max-w-2xl mx-auto">
                Common inquiries regarding the Cervello Eduverse ecosystem
              </p>
            </div>

            {/* Bespoke Expandable Accordion List */}
            <div className="max-w-3xl mx-auto space-y-4">
              {homeFAQs.map((faq, index) => {
                const isOpen = activeFaq === index;
                return (
                  <div key={index} className="nm-flat-light rounded-2xl border-transparent overflow-hidden transition-all duration-300">
                    <button
                      className="w-full text-left p-6 flex justify-between items-center bg-transparent focus:outline-none"
                      onClick={() => toggleFAQ(index)}
                      aria-expanded={isOpen}
                    >
                      <h3 className="font-bold text-base sm:text-lg text-[#0B1C2D]">{faq.question}</h3>
                      <span className={`text-[#C6A25E] text-2xl transition-transform duration-300 transform ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
                        ▾
                      </span>
                    </button>

                    {/* Smooth height transition accordion shell */}
                    <div
                      className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-48 opacity-100 border-t border-slate-200/30' : 'max-h-0 opacity-0 pointer-events-none'
                        }`}
                    >
                      <div className="p-6 text-[#1E293B] text-sm leading-relaxed nm-inset-light">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ================= CONTACT & LOCATION SECTION ================= */}
        <ContactSection />

        {/* Unified footer */}
        <Footer />

        {/* Unified floating action shortcuts */}
        <FloatingActions />
      </div>
    </React.Fragment>
  );
}

// Customized Program Card sub-component
function ProgramCard({ title, theme = "elevare", description, features, badgeText, btnLink, btnText, logoSrc }) {
  const themeConfigs = {
    junior: {
      badgeBg: "bg-sky-50 text-sky-600 border-sky-100/50",
      badgeHoverBg: "group-hover:bg-sky-100 group-hover:text-sky-700",
      glowColor: "from-sky-400/10 to-sky-500/5",
      hoverGlowColor: "group-hover:from-sky-400/25 group-hover:to-sky-500/15",
      glowBlob: "bg-sky-400/15",
      checkBg: "bg-sky-50 border-sky-100",
      checkColor: "text-sky-600",
      buttonBg: "bg-[#0B1C2D] hover:bg-sky-600 hover:shadow-[0_8px_20px_rgba(2,132,199,0.3)]",
      titleHover: "group-hover:text-sky-600",
      accentBorder: "group-hover:border-sky-300/30",
      arrowColor: "text-sky-500",
      spotlightColor: "rgba(56, 189, 248, 0.16)"
    },
    elevare: {
      badgeBg: "bg-slate-900/5 text-slate-800 border-slate-900/10",
      badgeHoverBg: "group-hover:bg-slate-900/10 group-hover:text-slate-950",
      glowColor: "from-[#0B1C2D]/5 to-[#1E293B]/2",
      hoverGlowColor: "group-hover:from-[#0B1C2D]/15 group-hover:to-[#1E293B]/10",
      glowBlob: "bg-indigo-400/10",
      checkBg: "bg-slate-100 border-slate-200",
      checkColor: "text-slate-700",
      buttonBg: "bg-[#0B1C2D] hover:bg-slate-800 hover:shadow-[0_8px_20px_rgba(11,28,45,0.3)]",
      titleHover: "group-hover:text-[#0B1C2D]",
      accentBorder: "group-hover:border-slate-300/30",
      arrowColor: "text-slate-600",
      spotlightColor: "rgba(30, 41, 59, 0.08)"
    },
    business: {
      badgeBg: "bg-[#C6A25E]/10 text-[#C6A25E] border-[#C6A25E]/20",
      badgeHoverBg: "group-hover:bg-[#C6A25E]/20 group-hover:text-[#9A7B3E]",
      glowColor: "from-[#C6A25E]/10 to-[#D4AF37]/5",
      hoverGlowColor: "group-hover:from-[#C6A25E]/25 group-hover:to-[#D4AF37]/15",
      glowBlob: "bg-amber-400/15",
      checkBg: "bg-amber-50 border-amber-100",
      checkColor: "text-[#C6A25E]",
      buttonBg: "bg-[#0B1C2D] hover:bg-[#C6A25E] hover:shadow-[0_8px_20px_rgba(198,162,94,0.3)]",
      titleHover: "group-hover:text-[#C6A25E]",
      accentBorder: "group-hover:border-[#C6A25E]/30",
      arrowColor: "text-[#C6A25E]",
      spotlightColor: "rgba(198, 162, 94, 0.16)"
    }
  };

  const style = themeConfigs[theme] || themeConfigs.elevare;

  const handleMouseMove = (e) => {
    const { currentTarget, clientX, clientY } = e;
    const rect = currentTarget.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    currentTarget.style.setProperty("--mouse-x", `${x}px`);
    currentTarget.style.setProperty("--mouse-y", `${y}px`);
  };

  return (
    <div
      onMouseMove={handleMouseMove}
      style={{ "--spotlight-color": style.spotlightColor }}
      className={`group relative nm-flat-light rounded-3xl border-transparent overflow-hidden hover:-translate-y-3 hover:nm-inset-light transition-all duration-500 flex flex-col justify-between spotlight-card`}
    >
      {/* Visual overlay dynamic gradient glow */}
      <div className={`absolute inset-0 bg-gradient-to-br ${style.glowColor} ${style.hoverGlowColor} opacity-70 transition-all duration-500 pointer-events-none z-0`} />

      {/* Decorative Blur Blob */}
      <div className={`absolute top-0 right-0 w-36 h-36 rounded-full blur-3xl ${style.glowBlob} opacity-30 group-hover:opacity-60 group-hover:scale-150 transition-all duration-700 pointer-events-none z-0`} />

      <div className="relative p-8 z-10 flex-1 flex flex-col justify-between">
        <div>
          {/* Logo container */}
          {logoSrc && (
            <div className="w-16 h-16 rounded-2xl nm-flat-light border-transparent flex items-center justify-center overflow-hidden mb-6 group-hover:scale-110 group-hover:rotate-2 group-hover:animate-float-medium transition-all duration-300">
              <LazyImage src={logoSrc} alt={title} className="w-full h-full object-cover" />
            </div>
          )}

          {/* Target Group Badge */}
          <div className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold mb-4 border transition-colors duration-300 ${style.badgeBg} ${style.badgeHoverBg}`}>
            {badgeText}
          </div>

          {/* Card Title with Interactive Arrow */}
          <h3 className={`text-2xl font-playfair font-bold text-[#0B1C2D] mb-3 flex items-center gap-2 ${style.titleHover} transition-colors duration-300`}>
            <span>{title}</span>
            <svg className={`w-5 h-5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 ${style.arrowColor} transition-all duration-300 flex-shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </h3>

          <p className="text-sm text-slate-600 leading-relaxed mb-6 font-light">{description}</p>
        </div>

        {/* Premium interactive checklist */}
        <ul className="space-y-3 mb-8">
          {features.map((item, idx) => (
            <li key={idx} className="flex items-start text-slate-700 text-sm font-medium group/item hover:translate-x-1 transition-transform duration-200">
              <div className={`w-5 h-5 rounded-full nm-inset-light flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 transition-transform duration-200 group-hover/item:scale-110`}>
                <svg className={`w-3.5 h-3.5 ${style.checkColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-slate-600 group-hover:text-slate-800 transition-colors duration-200">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="p-8 pt-0 z-10">
        <Link
          to={btnLink}
          className={`w-full nm-button-light py-3.5 px-6 rounded-xl font-semibold border-transparent transition-all duration-300 inline-block text-center active:scale-95 transform`}
        >
          {btnText}
        </Link>
      </div>
    </div>
  );
}

// Tech logos data for infinite loop marquee
const techLogos = [
  {
    name: "Intermediate MPC & BiPC",
    svg: (
      <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
      </svg>
    )
  },
  {
    name: "EAPCET & NEET Prep",
    svg: (
      <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  },
  {
    name: "Full-Stack Coding",
    svg: (
      <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    )
  },
  {
    name: "Data Analytics & AI",
    svg: (
      <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 002 2h2a2 2 0 002-2" />
      </svg>
    )
  },
  {
    name: "Startup Advisory",
    svg: (
      <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    )
  },
  {
    name: "Venture Capital Pitch",
    svg: (
      <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    )
  },
  {
    name: "Concept-First Mentorship",
    svg: (
      <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 .364l-.707 .707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 113.536 0V21h2v-2.172a5 5 0 013.536-8.828" />
      </svg>
    )
  },
  {
    name: "Placement Readiness",
    svg: (
      <svg className="w-8 h-8 text-[#C6A25E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    )
  }
];

// Slider visual helper
function HomepageSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const containerRef = useRef(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isSwiping = useRef(false);

  const desktopSlides = [
    "/img1.webp", "/img2.webp", "/img3.webp", "/img4.webp", "/img5.webp",
    "/img6.webp", "/img7.webp", "/img8.webp", "/img9.webp", "/img10.webp",
  ];

  const mobileSlides = [
    "/mimg1.webp", "/mimg2.webp", "/mimg3.webp", "/mimg4.webp", "/mimg5.webp",
    "/mimg6.webp", "/mimg7.webp", "/mimg8.webp", "/mimg9.webp", "/mimg10.webp",
    "/mimg11.webp", "/mimg12.webp",
  ];

  const slides = isMobile ? mobileSlides : desktopSlides;
  const aspectRatio = isMobile ? '3/2' : '5/2';

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isDragging) return; // Pause autoplay during swiping/dragging

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [slides.length, currentSlide, isDragging]);

  const handlePrev = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentSlide(index);
  };

  const handleDragStart = (e, clientX, clientY) => {
    if (e.type.startsWith('mouse') && e.button !== 0) return;

    isDraggingRef.current = true;
    setIsDragging(true);
    isSwiping.current = false;
    startXRef.current = clientX;
    startYRef.current = clientY;
    setDragOffset(0);
  };

  const handleDragMove = (e, clientX, clientY) => {
    if (!isDraggingRef.current) return;

    const diffX = clientX - startXRef.current;
    const diffY = clientY - startYRef.current;

    // Check if horizontal movement is stronger than vertical scroll
    if (Math.abs(diffX) > 10 || Math.abs(diffY) > 10) {
      if (Math.abs(diffX) > Math.abs(diffY)) {
        if (e.cancelable) {
          e.preventDefault();
        }
        isSwiping.current = true;
        setDragOffset(diffX);
      }
    }
  };

  const handleDragEnd = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);

    const minSwipeDistance = 50;

    if (isSwiping.current && Math.abs(dragOffset) > minSwipeDistance) {
      if (dragOffset < 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }

    setDragOffset(0);
    startXRef.current = 0;
    startYRef.current = 0;
  };

  const onMouseDown = (e) => handleDragStart(e, e.clientX, e.clientY);
  const onMouseMove = (e) => handleDragMove(e, e.clientX, e.clientY);
  const onMouseUp = () => handleDragEnd();
  const onMouseLeave = () => handleDragEnd();

  const onTouchStart = (e) => {
    const touch = e.touches[0];
    if (touch) {
      handleDragStart(e, touch.clientX, touch.clientY);
    }
  };
  const onTouchMove = (e) => {
    const touch = e.touches[0];
    if (touch) {
      handleDragMove(e, touch.clientX, touch.clientY);
    }
  };
  const onTouchEnd = () => handleDragEnd();

  return (
    <section
      className="relative mt-0 w-full max-h-[85vh] overflow-hidden bg-black select-none"
      style={{ aspectRatio, touchAction: 'pan-y' }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Slider container translating */}
      <div
        ref={containerRef}
        className="w-full h-full flex"
        style={{
          transform: `translateX(calc(-${currentSlide * 100}% + ${dragOffset}px))`,
          transition: isDragging ? 'none' : 'transform 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        }}
      >
        {slides.map((image, index) => (
          <div
            key={index}
            className="w-full h-full flex-shrink-0 relative select-none"
          >
            {/* Blurred background backup for widescreen aspect ratios */}
            <div
              className="absolute inset-0 bg-cover bg-center blur-2xl opacity-40 scale-110 pointer-events-none"
              style={{ backgroundImage: `url(${image})` }}
            />
            {/* Sharp centered image with subtle zoom transition */}
            <LazyImage
              src={image}
              alt={`Ecosystem Banner ${index + 1}`}
              className={`relative w-full h-full object-cover object-center pointer-events-none transition-transform duration-[4500ms] ease-out ${index === currentSlide && !isDragging ? "scale-102" : "scale-100"
                }`}
              fetchPriority={index === 0 ? "high" : "auto"}
              objectFit="cover"
            />
          </div>
        ))}
      </div>

      {/* Slider dots indicator */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex space-x-1.5 bg-black/25 backdrop-blur-xs px-3 py-2 rounded-full">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={(e) => handleDotClick(e, index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${index === currentSlide ? 'bg-[#C6A25E] w-4' : 'bg-white/40'
              }`}
            aria-label={`Show slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Slide Progress Bar */}
      {!isDragging && (
        <div
          key={currentSlide}
          className="absolute bottom-0 left-0 h-1 bg-[#C6A25E] z-20 animate-slide-progress"
        />
      )}
    </section>
  );
}

function ManifestoSection() {
  const beliefs = [
    {
      icon: (
        <svg className="w-9 h-9" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: "Spark Curiosity First",
      description: "We believe the best learning happens when questions matter more than answers. Our role isn't to fill minds with facts, but to ignite the desire to explore, understand, and create."
    },
    {
      icon: (
        <svg className="w-9 h-9" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      title: "Education Should Be Personal",
      description: "Every learner is unique. We reject one-size-fits-all approaches. Your pace, your style, your goals—these shape how we teach. Because personalized attention isn't a luxury; it's how real learning happens."
    },
    {
      icon: (
        <svg className="w-9 h-9" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      title: "Theory Without Practice Is Forgotten",
      description: "Knowledge only becomes powerful when applied. That's why every concept we teach is immediately connected to real-world use. You won't just learn—you'll build, create, and solve."
    },
    {
      icon: (
        <svg className="w-9 h-9" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138z" />
        </svg>
      ),
      title: "Excellence Is Non-Negotiable",
      description: "We don't cut corners. We don't compromise on quality. Every lesson, every resource, every interaction is crafted with care. Because you deserve nothing less than our absolute best."
    },
    {
      icon: (
        <svg className="w-9 h-9" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: "Mentorship Transforms Lives",
      description: "Teachers inform; mentors transform. We're not here to lecture from a distance. We walk alongside you—guiding, challenging, celebrating breakthroughs, and supporting through struggles."
    },
    {
      icon: (
        <svg className="w-9 h-9" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Learning Has No Boundaries",
      description: "Education shouldn't be limited by geography or circumstance. We're building bridges—connecting students in Hyderabad to opportunities worldwide, breaking barriers one learner at a time."
    }
  ];

  return (
    <section className="py-28 bg-nm-dark text-[#0B1C2D] relative overflow-hidden border-t border-slate-200/20">
      {/* Background visual nodes */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#C6A25E] rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-sky-400 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold mb-4 text-[#0B1C2D]">
            What We Believe
          </h2>
          <p className="text-lg text-slate-700 max-w-2xl mx-auto font-light">
            These aren't marketing slogans. They're the principles that guide every decision we make.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {beliefs.map((belief, index) => (
            <div
              key={index}
              className="group relative nm-flat-dark p-8 rounded-3xl border-transparent hover:-translate-y-2.5 transition-all duration-500 overflow-hidden hover:nm-inset-dark"
            >
              {/* Subtle background glow */}
              <div className="absolute -top-10 -right-10 w-24 h-24 rounded-full bg-[#C6A25E]/5 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500 pointer-events-none" />

              <div className="text-[#C6A25E] mb-6 w-14 h-14 rounded-2xl nm-inset-dark border-transparent flex items-center justify-center group-hover:scale-110 transition-all duration-300">
                {belief.icon}
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#0B1C2D] group-hover:text-[#C6A25E] transition-colors duration-300">{belief.title}</h3>
              <p className="text-sm text-slate-700 leading-relaxed font-light">{belief.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FoundersStorySection() {
  return (
    <section className="py-24 bg-nm-light relative overflow-hidden border-t border-slate-200/40">
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#C6A25E]/5 to-transparent rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-sky-400/5 to-transparent rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold mb-4 text-[#0B1C2D]">
            Why I Started This
          </h2>
          <p className="text-slate-500 max-w-xl mx-auto text-base">
            A personal story about frustration, failure, and finding purpose
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Story Panels */}
          <div className="space-y-6">
            <div className="nm-flat-light p-8 rounded-3xl border-l-4 border-[#C6A25E] border-t-transparent border-r-transparent border-b-transparent shadow-sm">
              <h3 className="text-lg font-bold mb-3 text-[#0B1C2D] flex items-center">
                <span className="w-2.5 h-2.5 rounded-full bg-[#C6A25E] mr-2.5 flex-shrink-0" />
                The Problem I Couldn't Ignore
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed font-light">
                Growing up in Hyderabad, I saw something that never sat right with me: brilliant students around me were memorizing textbooks but couldn't apply what they'd learned. They could ace exams but froze when faced with real problems. I experienced this myself—feeling completely unprepared for real-world execution.
              </p>
            </div>

            <div className="nm-flat-dark text-[#0B1C2D] p-8 rounded-3xl border-transparent shadow-md hover:scale-[1.01] transition-all duration-300">
              <h3 className="text-lg font-bold mb-3 text-[#C6A25E] flex items-center">
                <span className="w-2.5 h-2.5 rounded-full bg-[#C6A25E] mr-2.5 flex-shrink-0" />
                The Breaking Point
              </h3>
              <p className="text-slate-700 text-sm leading-relaxed font-light">
                The moment that changed everything: A student I was mentoring had scored 95% in her board exams but couldn't write a simple email or think critically about her career options. She asked me, "What's the point of all these marks if I don't know what to do next?" That question hit me hard.
              </p>
            </div>

            <div className="nm-flat-light p-8 rounded-3xl border-transparent hover:-translate-y-0.5 transition-all duration-300">
              <h3 className="text-lg font-bold mb-3 text-[#0B1C2D] flex items-center">
                <span className="w-2.5 h-2.5 rounded-full bg-[#C6A25E] mr-2.5 flex-shrink-0" />
                What We're Building
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed font-light">
                Cervello Eduverse wasn't born from a desire to do education differently. Not another standard coaching center, but an ecosystem where academic conceptual structures meet real, validated project development.
              </p>
            </div>
          </div>

          {/* Founder visual card */}
          <div className="space-y-6">
            <div className="nm-flat-dark text-[#0B1C2D] rounded-3xl p-8 border-transparent flex flex-col items-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#C6A25E]/20 to-transparent rounded-full blur-xl pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-sky-500/5 to-transparent rounded-full blur-xl pointer-events-none" />

              <div className="relative z-10 flex flex-col items-center">
                <div className="w-40 h-40 rounded-3xl overflow-hidden mb-6 border-2 border-[#C6A25E] shadow-2xl relative group">
                  <LazyImage
                    src="/founder.webp"
                    alt="Mohd. Yaasir Sufiyan"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                <div className="text-center">
                  <h3 className="text-2xl font-bold text-[#0B1C2D] mb-1">Mohd. Yaasir Sufiyan</h3>
                  <p className="text-sm text-[#C6A25E] font-semibold uppercase tracking-wider mb-6">Founder & CEO</p>

                  <blockquote className="text-base font-playfair font-medium italic text-slate-700 leading-relaxed max-w-sm border-t border-slate-200/30 pt-6">
                    "Education is not just about passing exams—it's about building the foundation for a lifetime of learning. When we nurture concepts first, capability naturally follows."
                  </blockquote>
                </div>
              </div>
            </div>

            <div className="nm-flat-light rounded-3xl p-6 border-transparent hover:scale-[1.01] transition-all duration-300 relative overflow-hidden">
              <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-[#C6A25E]/5 rounded-full blur-xl pointer-events-none" />
              <span className="text-3xl text-[#C6A25E] block mb-3 font-serif">“</span>
              <p className="italic text-slate-600 text-sm leading-relaxed relative z-10">
                "We'd rather stay focused and deliver excellence than grow too fast and compromise on outcomes. Every student who trusts us gets our absolute best."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyWereNewSection() {
  const advantages = [
    {
      title: "We Move Fast",
      description: "See a better way to teach something? We implement it next week, not next year. Quick updates based on student outcomes.",
      icon: "⚡"
    },
    {
      title: "Every Student Matters",
      description: "We focus on a personalized mentorship structure. Your specific feedback shapes our programs directly.",
      icon: "🎯"
    },
    {
      title: "Quality Over Scale",
      description: "We'd rather stay small and excellent than grow quickly and become mediocre. Standard quality is non-negotiable.",
      icon: "💎"
    }
  ];

  return (
    <section id="why-us" className="py-24 bg-nm-light relative overflow-hidden border-t border-slate-200/40">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#0B1C2D]">
            Our Commitment
          </h2>
          <p className="text-sm text-slate-500 max-w-lg mx-auto">
            Focusing on standard quality, individual mentorship, and conceptual clarity.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {advantages.map((item, index) => (
            <div
              key={index}
              className="nm-flat-light p-8 rounded-3xl border-transparent hover:nm-inset-light transition-all duration-300 hover:-translate-y-1"
            >
              <div className="text-3xl mb-4">{item.icon}</div>
              <h3 className="text-lg font-bold mb-2 text-[#0B1C2D]">{item.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed font-light">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [mapActive, setMapActive] = useState(false);

  return (
    <section id="contact" className="py-16 md:py-24 bg-nm-light border-t border-slate-200/40" aria-label="Contact and Location Map">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur-md border border-white shadow-md mb-6">
            <span className="w-2 h-2 rounded-full bg-[#C6A25E] animate-pulse" />
            <span className="text-xs sm:text-sm font-medium text-slate-700">Find Us / Reach Out</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-[#0B1C2D] tracking-tight">
            Contact & <span className="bg-gradient-to-r from-[#C6A25E] to-sky-500 bg-clip-text text-transparent">Location</span>
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-slate-500 max-w-2xl mx-auto font-light">
            Visit our state-of-the-art learning lab in Hyderabad or connect with our support desk.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 md:gap-8 lg:gap-12 items-stretch">
          {/* Contact Details Card */}
          <div className="lg:col-span-5 flex flex-col justify-between space-y-6">
            <div className="nm-flat-light p-5 xs:p-6 sm:p-8 rounded-3xl border-transparent flex-1 flex flex-col justify-between">
              <div className="space-y-6 sm:space-y-8">
                {/* Address */}
                <div className="flex gap-3 sm:gap-4 items-start">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl nm-inset-light flex items-center justify-center text-[#C6A25E] text-xl sm:text-2xl flex-shrink-0">
                    📍
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm sm:text-base font-bold text-[#0B1C2D]">Our Campus Address</h4>
                    <a
                      href="https://www.google.com/maps/place/Cervello+Eduverse+%7CCervello+Junior+Academy+%7CCervello+elevare+academy/@17.3553111,78.5057216,17z/data=!3m1!4b1!4m6!3m5!1s0x3bcb971b9eec41df:0xe134346aa0d8a993!8m2!3d17.3553111!4d78.5057216!16s%2Fg%2F11ytp6j2b2"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs sm:text-sm text-slate-500 hover:text-[#C6A25E] mt-1 leading-relaxed break-words block transition-colors"
                    >
                      Santoshnagar-Saidabad Main road, 5th Floor, Emaan Manzil, Royal Embassy School Lane, beside Success Group of Institutions, Hyderabad, Telangana - 500059
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex gap-3 sm:gap-4 items-start">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl nm-inset-light flex items-center justify-center text-sky-600 text-xl sm:text-2xl flex-shrink-0">
                    ✉️
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm sm:text-base font-bold text-[#0B1C2D]">Email Inquiries</h4>
                    <a href="mailto:cervelloeduverse@gmail.com" className="text-xs sm:text-sm text-slate-600 hover:text-[#C6A25E] mt-1 block transition-colors break-all">
                      cervelloeduverse@gmail.com
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex gap-3 sm:gap-4 items-start">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl nm-inset-light flex items-center justify-center text-emerald-600 text-xl sm:text-2xl flex-shrink-0">
                    📞
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm sm:text-base font-bold text-[#0B1C2D]">Support Hotline</h4>
                    <a href="tel:+918639619079" className="text-xs sm:text-sm text-slate-600 hover:text-[#C6A25E] mt-1 block transition-colors break-words">
                      +91 86396 19079
                    </a>
                  </div>
                </div>
              </div>

              <div className="pt-6 sm:pt-8 border-t border-slate-200/30 mt-6 sm:mt-8">
                <a
                  href="/book-demo"
                  className="w-full nm-button-light py-3.5 sm:py-4 rounded-xl font-bold border-transparent text-center block text-sm sm:text-base hover:scale-[1.02]"
                >
                  Book 7-Day Demo (₹99)
                </a>
              </div>
            </div>
          </div>

          {/* Interactive Google Map frame */}
          <div className="lg:col-span-7 min-h-[300px] sm:min-h-[350px] lg:min-h-[450px]">
            <div
              className="w-full h-full rounded-3xl overflow-hidden nm-flat-light border-transparent relative group cursor-pointer"
              onClick={() => setMapActive(true)}
              onMouseLeave={() => setMapActive(false)}
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3808.2045582315783!2d78.5031467!3d17.3553111!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb971b9eec41df%3A0xe134346aa0d8a993!2sCervello%20Eduverse%20%7C%20Cervello%20Junior%20Academy%20%7C%20Cervello%20elevare%20academy!5e0!3m2!1sen!2sin!4v1718970000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className={`w-full h-full border-0 absolute inset-0 transition-all duration-300 ${mapActive ? 'pointer-events-auto' : 'pointer-events-none'}`}
                title="Cervello Eduverse Location Map"
              />
              {!mapActive && (
                <div className="absolute inset-0 bg-[#0B1C2D]/15 backdrop-blur-[1px] flex items-center justify-center pointer-events-none transition-all duration-300">
                  <div className="px-4 py-2.5 rounded-full bg-[#0B1C2D]/95 text-white text-xs font-semibold shadow-md flex items-center gap-1.5 border border-white/20">
                    <span>📍</span> Click / Tap to interact with map
                  </div>
                </div>
              )}
              {mapActive && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMapActive(false);
                  }}
                  className="absolute top-4 right-4 z-30 px-3 py-1.5 rounded-xl bg-[#0B1C2D]/90 text-white text-xs font-semibold shadow-md flex items-center gap-1 border border-white/20 hover:bg-[#C6A25E] transition-colors"
                >
                  🔒 Lock Map
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default CervelloEduverseLanding;